import React, { useState, useMemo } from 'react'
import { Table, Button, Modal, Form, Input, InputNumber, Switch, message, Popconfirm, Space, Tag } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import TableComponent from '../Table'

const Container = styled.div`
  width: 100%;
  padding: 20px;
`

const HeaderWrapper = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
`

function KnowledgeVideosTable({
    dataSource,
    loading,
    onCreate,
    onUpdate,
    onDelete,
    onRefresh,
    ...rest
}) {
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [editingRecord, setEditingRecord] = useState(null)
    const [form] = Form.useForm()

    const handleAdd = () => {
        setEditingRecord(null)
        form.resetFields()
        setIsModalVisible(true)
    }

    const handleEdit = (record) => {
        setEditingRecord(record)
        form.setFieldsValue(record)
        setIsModalVisible(true)
    }

    const handleDelete = async (id) => {
        const success = await onDelete(id)
        if (success) {
            onRefresh()
        }
    }

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields()
            let success = false

            if (editingRecord) {
                success = await onUpdate(editingRecord.id, values)
            } else {
                success = await onCreate(values)
            }

            if (success) {
                setIsModalVisible(false)
                form.resetFields()
                setEditingRecord(null)
                onRefresh()
            }
        } catch (error) {
            console.error('Validation failed:', error)
        }
    }

    const handleModalCancel = () => {
        setIsModalVisible(false)
        form.resetFields()
        setEditingRecord(null)
    }

    const getVideoUrl = (videoId) => {
        if (!videoId) return ''
        if (videoId.startsWith('http://') || videoId.startsWith('https://')) {
            return videoId
        }
        return `https://www.youtube.com/watch?v=${videoId}`
    }

    const columns = [
        {
            title: 'ลำดับ',
            dataIndex: 'order',
            key: 'order',
            width: 100,
            sorter: (a, b) => a.order - b.order,
            defaultSortOrder: 'ascend',
        },
        {
            title: 'ชื่อคลิปวิดีโอ',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
        },
        {
            title: 'คำอธิบาย',
            dataIndex: 'desc',
            key: 'desc',
            ellipsis: true,
        },
        {
            title: 'Video ID/Link',
            dataIndex: 'videoId',
            key: 'videoId',
            ellipsis: true,
            render: (text) => {
                const url = getVideoUrl(text)
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                        {text}
                    </a>
                )
            },
        },
        {
            title: 'สถานะ',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (isActive) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                </Tag>
            ),
        },
        {
            title: 'จัดการ',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEdit(record)}
                    >
                        แก้ไข
                    </Button>
                    <Popconfirm
                        title="คุณแน่ใจหรือไม่ที่จะลบคลิปวิดีโอนี้?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="ใช่"
                        cancelText="ไม่"
                    >
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        >
                            ลบ
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    return (
        <Container>
            <HeaderWrapper>
                <h2>จัดการคลิปวิดีโอ</h2>
                <ButtonWrapper>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        เพิ่มคลิปวิดีโอ
                    </Button>
                </ButtonWrapper>
            </HeaderWrapper>

            <TableComponent
                columns={columns}
                dataSource={useMemo(() => {
                    return [...dataSource].sort((a, b) => (a.order || 0) - (b.order || 0))
                }, [dataSource])}
                loading={loading}
                rowKey="id"
                scroll={{ x: 1200 }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                }}
                {...rest}
            />

            <Modal
                title={editingRecord ? 'แก้ไขคลิปวิดีโอ' : 'เพิ่มคลิปวิดีโอ'}
                visible={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="บันทึก"
                cancelText="ยกเลิก"
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        isActive: true,
                        order: 1,
                        ...editingRecord,
                    }}
                >
                    <Form.Item
                        name="title"
                        label="ชื่อคลิปวิดีโอ"
                        rules={[
                            {
                                required: true,
                                message: 'กรุณากรอกชื่อคลิปวิดีโอ',
                            },
                        ]}
                    >
                        <Input placeholder="กรอกชื่อคลิปวิดีโอ" />
                    </Form.Item>

                    <Form.Item
                        name="desc"
                        label="คำอธิบาย"
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder="กรอกคำอธิบาย"
                        />
                    </Form.Item>

                    <Form.Item
                        name="videoId"
                        label="Video ID หรือ Link YouTube"
                        rules={[
                            {
                                required: true,
                                message: 'กรุณากรอก Video ID หรือ Link YouTube',
                            },
                        ]}
                        extra="กรอก YouTube ID (เช่น UYuWx9Sn3Zo) หรือ Link YouTube (เช่น https://www.youtube.com/watch?v=UYuWx9Sn3Zo)"
                    >
                        <Input placeholder="UYuWx9Sn3Zo หรือ https://www.youtube.com/watch?v=UYuWx9Sn3Zo" />
                    </Form.Item>

                    <Form.Item
                        name="order"
                        label="ลำดับการแสดงผล"
                        rules={[
                            {
                                required: true,
                                message: 'กรุณากรอกลำดับการแสดงผล',
                            },
                        ]}
                    >
                        <InputNumber
                            min={1}
                            placeholder="กรอกลำดับ"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label="สถานะ"
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="เปิดใช้งาน" unCheckedChildren="ปิดใช้งาน" />
                    </Form.Item>
                </Form>
            </Modal>
        </Container>
    )
}

export default KnowledgeVideosTable

